export const FUNNEL_EVENTS = [
  "agentready_page_view",
  "agentready_scan_started",
  "agentready_scan_preset_selected",
  "agentready_scan_completed",
  "agentready_scan_failed",
  "agentready_report_viewed",
  "agentready_improvements_opened",
  "agentready_finding_viewed",
  "agentready_prompt_copied",
  "agentready_expert_help_selected",
  "agentready_expert_help_removed",
  "agentready_quote_cart_opened",
  "agentready_quote_started",
  "agentready_quote_submitted",
  "agentready_quote_failed",
  "agentready_quote_won",
  "agentready_category_expanded",
  "agentready_rescan_started",
  "agentready_question_scanned",
  "agentready_question_skipped",
  "agentready_verdict_shown",
  "agentready_question_fix",
  "agentready_question_continue",
  "agentready_summary_reached",
  "agentready_results_emailed",
  "agentready_report_shared",
  "agentready_scan_cta_clicked",
  "agentready_people_toggle",
  "agentready_fix_cta_clicked",
  "agentready_path_self_serve",
  "agentready_path_white_glove",
  "agentready_demo_requested",
  "agentready_email_modal_shown",
  "agentready_email_modal_dismissed",
  "agentready_audit_downloaded",
  "agentready_walk_started",
  "agentready_walk_completed",
  "agentready_benchmark_expanded",
  "agentready_onboarding_started_from_scan",
  "agentready_activate_clicked",
  "agentready_channels_selected",
  "agentready_tab_changed",
  "agentready_scorecard_clicked",
  "agentready_issue_expanded",
  "agentready_fix_prompt_copied",
  "agentready_fix_package_downloaded",
  "agentready_pdf_exported",
  "agentready_csv_exported",
  "agentready_managed_service_started",
  "agentready_managed_service_submitted",
] as const;

export type FunnelEventName = (typeof FUNNEL_EVENTS)[number];

const PII_KEYS = ["email", "phone", "notes", "contactName", "businessName", "name"] as const;

export type FunnelAttribution = {
  first: Record<string, string> | null;
  last: Record<string, string> | null;
} | null;

export type FunnelEventInput = {
  name: FunnelEventName;
  sessionId: string;
  scanId?: string | null;
  rfpId?: string | null;
  domain?: string | null;
  findingId?: string | null;
  category?: string | null;
  classification?: string | null;
  assessment?: string | null;
  improvementCount?: number | null;
  selectedCount?: number | null;
  solutionGroup?: string | null;
  attribution?: FunnelAttribution;
};

export function isFunnelEventName(value: string): value is FunnelEventName {
  return (FUNNEL_EVENTS as readonly string[]).includes(value);
}

export function publicFunnelProps(input: Record<string, unknown>): Record<string, string | number | boolean> {
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(input)) {
    if ((PII_KEYS as readonly string[]).includes(key)) continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      safe[key] = value;
    }
  }
  return safe;
}

export function funnelDedupeKey(input: FunnelEventInput): string {
  const sessionId = input.sessionId || "server";
  const scanId = input.scanId || "";
  const findingId = input.findingId || "";
  if (input.name === "agentready_scan_completed" || input.name === "agentready_scan_failed") {
    return scanId;
  }
  if (input.name === "agentready_quote_submitted" || input.name === "agentready_quote_won") {
    return input.rfpId || scanId;
  }
  if (
    input.name === "agentready_page_view" ||
    input.name === "agentready_report_viewed" ||
    input.name === "agentready_improvements_opened" ||
    input.name === "agentready_quote_cart_opened" ||
    input.name === "agentready_quote_started" ||
    input.name === "agentready_finding_viewed" ||
    input.name === "agentready_prompt_copied" ||
    input.name === "agentready_expert_help_selected" ||
    input.name === "agentready_expert_help_removed" ||
    input.name === "agentready_category_expanded"
  ) {
    return `${sessionId}:${scanId}:${findingId}`;
  }
  if (
    input.name === "agentready_question_scanned" ||
    input.name === "agentready_question_skipped" ||
    input.name === "agentready_verdict_shown" ||
    input.name === "agentready_question_fix" ||
    input.name === "agentready_question_continue" ||
    input.name === "agentready_summary_reached" ||
    input.name === "agentready_results_emailed" ||
    input.name === "agentready_report_shared" ||
    input.name === "agentready_fix_cta_clicked" ||
    input.name === "agentready_path_self_serve" ||
    input.name === "agentready_path_white_glove" ||
    input.name === "agentready_demo_requested" ||
    input.name === "agentready_email_modal_shown" ||
    input.name === "agentready_email_modal_dismissed" ||
    input.name === "agentready_audit_downloaded" ||
    input.name === "agentready_walk_started" ||
    input.name === "agentready_walk_completed" ||
    input.name === "agentready_benchmark_expanded" ||
    input.name === "agentready_onboarding_started_from_scan" ||
    input.name === "agentready_channels_selected" ||
    input.name === "agentready_tab_changed"
  ) {
    return `${input.name}:${sessionId}:${scanId}:${findingId}:${input.classification ?? ""}`;
  }
  return `${sessionId}:${scanId}:${findingId}:${Date.now()}`;
}

export function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function reuseRecentRfp(createdAt: Date, now = Date.now()): boolean {
  return now - createdAt.getTime() < 60 * 60 * 1000;
}
